import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { BookingCalendar } from '../../components/feature/BookingCalendar';

export default function BookAppointmentPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const appointmentTypes = [
    {
      title: 'ADHD Clarity Evaluation',
      duration: '90-minute comprehensive evaluation',
      includes: [
        'ADHD diagnostic evaluation',
        'Discussion of symptoms and history',
        'Treatment recommendations',
        'Medication discussion if appropriate',
      ],
    },
    {
      title: 'Follow-Up Appointment',
      duration: '30-minute session',
      includes: [
        'Medication management',
        'Symptom review',
        'Treatment adjustments',
        'Ongoing support',
      ],
    },
    {
      title: 'Coaching Intake Call',
      duration: '20-minute introductory call',
      includes: [
        'Explore ADHD coaching with Lauren',
        'Discuss your goals and challenges',
        'Determine if coaching is the right fit',
        'Learn about the coaching process',
      ],
    },
  ];

  const steps = [
    {
      icon: 'ri-calendar-check-line',
      title: 'Schedule',
      description: 'Choose a time that works for you using the online scheduling system.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Prepare',
      description: 'You will receive a confirmation email with intake forms and instructions before your appointment.',
    },
    {
      icon: 'ri-video-chat-line',
      title: 'Your Evaluation',
      description: 'Meet with Lauren via secure telehealth for a comprehensive ADHD evaluation and treatment discussion.',
    },
  ];

  const importantNotes = [
    'Telehealth appointments are available for women located in Oregon.',
    'New patients should begin with the ADHD Clarity Evaluation.',
    'Please complete intake paperwork prior to your appointment.',
    'If ADHD is not the primary diagnosis, Lauren can still help evaluate and treat other psychiatric concerns.',
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/43e0956c3b4abb84081ae8606d3e77e1.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E2D5E]/40 via-[#1E2D5E]/30 to-[#1E2D5E]/40" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-[#FAF7F2] mb-6 font-['Cormorant_Garamond']">
            Book Your Appointment
          </h1>
          <p className="text-xl md:text-2xl text-[#FAF7F2]/90 mb-4 font-light">
            Start with clarity and take the first step toward understanding your ADHD brain.
          </p>
          <p className="text-base md:text-lg text-[#FAF7F2]/80 mb-10 max-w-2xl mx-auto">
            Appointments are conducted via secure telehealth for women located in Oregon.
          </p>
          <a
            href="#schedule"
            className="inline-flex px-10 py-4 bg-[#C9A96E] text-[#FAF7F2] rounded-full text-base font-medium tracking-wide hover:bg-[#B89858] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Schedule Your ADHD Evaluation
          </a>
        </div>
      </section>

      {/* What Happens Next Section */}
      <section className="py-16 md:py-24 px-5 md:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-[#1E2D5E] text-center mb-16 font-['Cormorant_Garamond']">
            What Happens Next
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#C9A96E]/10 rounded-full">
                  <i className={`${step.icon} text-3xl text-[#C9A96E]`} />
                </div>
                <h3 className="text-2xl font-medium text-[#1E2D5E] mb-4 font-['Cormorant_Garamond']">
                  {step.title}
                </h3>
                <p className="text-base text-[#1E2D5E]/70 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Appointment Options Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#F5F1E8]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-[#1E2D5E] text-center mb-16 font-['Cormorant_Garamond']">
            Appointment Options
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {appointmentTypes.map((type, index) => (
              <div
                key={index}
                className="bg-[#FAF7F2] rounded-2xl p-8 md:p-10 border border-[#C9A96E]/20 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <h3 className="text-2xl md:text-3xl font-medium text-[#1E2D5E] mb-2 font-['Cormorant_Garamond']">
                  {type.title}
                </h3>
                <p className="text-sm text-[#C9A96E] mb-6 font-medium tracking-wide">
                  {type.duration}
                </p>
                <div className="space-y-3">
                  <p className="text-sm font-medium text-[#1E2D5E] mb-4">Includes:</p>
                  {type.includes.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-[#C9A96E] text-lg mt-0.5 flex-shrink-0" />
                      <p className="text-base text-[#1E2D5E]/80 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notes Section */}
      <section className="py-16 md:py-24 px-5 md:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-[#1E2D5E] text-center mb-12 font-['Cormorant_Garamond']">
            Before You Book
          </h2>
          <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 border border-[#C9A96E]/20 shadow-sm">
            <div className="space-y-5">
              {importantNotes.map((note, index) => (
                <div key={index} className="flex items-start gap-4">
                  <i className="ri-information-line text-[#C9A96E] text-xl mt-0.5 flex-shrink-0" />
                  <p className="text-base text-[#1E2D5E]/80 leading-relaxed">{note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Booking Calendar Section */}
      <section id="schedule" className="py-16 md:py-24 px-5 md:px-8 bg-[#F5F1E8]">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-light text-[#1E2D5E] mb-6 font-['Cormorant_Garamond']">
            Schedule Your Appointment
          </h2>
          <p className="text-lg text-[#1E2D5E]/80 leading-relaxed">
            Choose a date and time that works best for you. We'll send you a confirmation email with all the details.
          </p>
        </div>
        <BookingCalendar
          timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/e63f2abd-55f3-47d7-a6bd-73e7c21d1f01.48c96a31e2f1530bcecd2bac0fbbe36266706535112584fd811d31efa3737452"
          appointmentsApi="https://readdy.ai/api/public/calendar/appointments/e63f2abd-55f3-47d7-a6bd-73e7c21d1f01.48c96a31e2f1530bcecd2bac0fbbe36266706535112584fd811d31efa3737452"
          texts={{
            selectDate: 'Select Your Date',
            chooseTime: 'Choose a Time',
            reviewDetails: 'Your Appointment Details',
            fullName: 'Full Name',
            phoneNumber: 'Phone Number',
            confirmBooking: 'Confirm Booking',
            modifyTime: 'Modify Time',
            back: 'Back',
            noSlotsAvailable: 'No time slots available for this date.',
            bookingSuccess: 'Your appointment has been successfully booked! You will receive a confirmation email shortly.',
            bookingError: 'Unable to complete your booking. Please try again or contact us for assistance.',
            loading: 'Loading available appointments...',
          }}
          containerStyle="px-5 md:px-8"
        />
      </section>

      {/* Footer */}
      <footer className="bg-[#1E2D5E] text-[#FAF7F2] py-12 px-5 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-8">
            <div>
              <h3 className="text-lg font-medium mb-4 font-['Cormorant_Garamond']">North Star ADHD Psychiatry</h3>
              <p className="text-sm text-[#FAF7F2]/70 leading-relaxed">
                Compassionate psychiatric care for women with ADHD in Oregon.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-4 font-['Cormorant_Garamond']">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/adhd-evaluations" className="block text-sm text-[#FAF7F2]/70 hover:text-[#C9A96E] transition-colors cursor-pointer">
                  ADHD Evaluations
                </Link>
                <Link to="/membership" className="block text-sm text-[#FAF7F2]/70 hover:text-[#C9A96E] transition-colors cursor-pointer">
                  Membership
                </Link>
                <Link to="/coaching" className="block text-sm text-[#FAF7F2]/70 hover:text-[#C9A96E] transition-colors cursor-pointer">
                  Coaching
                </Link>
                <Link to="/meet-lauren" className="block text-sm text-[#FAF7F2]/70 hover:text-[#C9A96E] transition-colors cursor-pointer">
                  Meet Lauren
                </Link>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-4 font-['Cormorant_Garamond']">Contact</h3>
              <p className="text-sm text-[#FAF7F2]/70 leading-relaxed">
                Serving women in Oregon via secure telehealth
              </p>
            </div>
          </div>
          <div className="border-t border-[#FAF7F2]/20 pt-8 text-center">
            <p className="text-sm text-[#FAF7F2]/60">
              © 2025 North Star ADHD Psychiatry for Women. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}